/**
 * NightOwlBanner — after-hours support notice.
 *
 * Config is fetched from /api/support-hours which returns:
 *   - is_active (server-computed, respects mode + start/end + America/Chicago)
 *   - pill_label, title, body, cta_text (admin-editable)
 *   - start_hour, end_hour (for the "10 PM – 8 AM" sub-label)
 *
 * Preview override: append `?nightowl=1` to any URL to force-show the banner
 * (useful for admin QA + previewing during the day).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Moon, Sparkles, ArrowDown } from 'lucide-react';
import { motion } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const scrollToLoadCard = () => {
    const el = document.querySelector('[data-testid="load-money-home-card"]')
            || document.querySelector('[data-testid="load-money-pill"]')
            || document.querySelector('[data-testid="load-money-btn"]');
    if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-4', 'ring-[#FFD700]', 'ring-offset-2', 'ring-offset-black');
        setTimeout(() => {
            el.classList.remove('ring-4', 'ring-[#FFD700]', 'ring-offset-2', 'ring-offset-black');
        }, 2500);
    }
};

const forceOverride = () => {
    try { return new URLSearchParams(window.location.search).get('nightowl') === '1'; }
    catch { return false; }
};

const fmtHour = (h) => {
    const n = Number(h);
    if (!Number.isFinite(n)) return '';
    if (n === 0) return '12 AM';
    if (n < 12) return `${n} AM`;
    if (n === 12) return '12 PM';
    return `${n - 12} PM`;
};

export default function NightOwlBanner() {
    const [cfg, setCfg] = useState(null);
    const [override] = useState(forceOverride);

    // Fetch server config on mount + poll every 90s so the banner appears/disappears
    // exactly on the schedule boundary and picks up admin edits within ~90s.
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await axios.get(`${API}/support-hours`);
                if (!cancelled) setCfg(data);
            } catch { /* silent — banner just stays hidden if API is down */ }
        };
        load();
        const t = setInterval(load, 90_000);
        return () => { cancelled = true; clearInterval(t); };
    }, []);

    if (!cfg) return null;
    const show = override || cfg.is_active;
    if (!show) return null;

    const subLabel = `${fmtHour(cfg.start_hour)} – ${fmtHour(cfg.end_hour)} · Central Time`;

    return (
        <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="relative overflow-hidden rounded-2xl border-2 border-[#FFD700]/70 shadow-[0_0_28px_rgba(255,215,0,0.25)]"
            style={{
                background: 'linear-gradient(135deg, #0b1030 0%, #1a1348 45%, #3a1a5c 100%)',
            }}
            data-testid="night-owl-banner"
        >
            {/* Twinkling star field */}
            <div className="absolute inset-0 pointer-events-none opacity-70">
                {[...Array(14)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute rounded-full bg-[#FFF9C4]"
                        style={{
                            width: 2 + (i % 3),
                            height: 2 + (i % 3),
                            left: `${(i * 37) % 95 + 2}%`,
                            top: `${(i * 53) % 80 + 5}%`,
                            boxShadow: '0 0 6px rgba(255,249,196,0.9)',
                        }}
                        animate={{ opacity: [0.3, 1, 0.3] }}
                        transition={{ duration: 1.8 + (i % 5) * 0.3, repeat: Infinity, delay: i * 0.15 }}
                    />
                ))}
            </div>

            {/* Pulsing golden border overlay */}
            <motion.div
                className="absolute inset-0 rounded-2xl pointer-events-none"
                animate={{ boxShadow: [
                    'inset 0 0 20px rgba(255,215,0,0.15)',
                    'inset 0 0 32px rgba(255,215,0,0.35)',
                    'inset 0 0 20px rgba(255,215,0,0.15)',
                ] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
            />

            <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 sm:p-5">
                {/* Moon icon with soft glow */}
                <div className="shrink-0 relative">
                    <motion.div
                        className="w-14 h-14 rounded-full bg-gradient-to-br from-[#FFF9C4] to-[#FFB800] flex items-center justify-center"
                        animate={{ boxShadow: [
                            '0 0 20px rgba(255,215,0,0.5)',
                            '0 0 34px rgba(255,215,0,0.85)',
                            '0 0 20px rgba(255,215,0,0.5)',
                        ] }}
                        transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                    >
                        <Moon className="w-7 h-7 text-[#0b1030]" strokeWidth={2.5} />
                    </motion.div>
                    <Sparkles className="absolute -top-1 -right-1 w-4 h-4 text-[#FFD700]" />
                </div>

                {/* Copy */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-black uppercase tracking-[0.25em] text-[#FFD700] bg-[#FFD700]/10 px-2 py-0.5 rounded-full border border-[#FFD700]/50">
                            {cfg.pill_label}
                        </span>
                        {cfg.mode === 'auto' && (
                            <span className="text-[10px] text-[#c8b8e8]">{subLabel}</span>
                        )}
                    </div>
                    <h3 className="mt-1.5 text-xl sm:text-2xl font-black text-white leading-tight">
                        {cfg.title}
                    </h3>
                    <p className="mt-1 text-sm text-[#e8def5] leading-snug whitespace-pre-line">
                        {cfg.body}
                    </p>
                </div>

                {/* CTA */}
                <button
                    onClick={scrollToLoadCard}
                    className="shrink-0 w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-[#FFD700] to-[#FF8C00] text-black font-black text-sm shadow-lg hover:brightness-110 active:scale-95 transition-all"
                    data-testid="night-owl-load-btn"
                >
                    <ArrowDown className="w-4 h-4" />
                    {cfg.cta_text}
                </button>
            </div>
        </motion.div>
    );
}
